import { useId } from 'react';

const SIZE = 44;
const LEFT = 30;
const TOP = 38;
const INK = '#24384b';
const BLUE = '#185abd';
const RUST = '#9a3412';

export const battleshipCellLabel = (cell) => `R${Math.floor(cell / 10) + 1}C${cell % 10 + 1}`;
export const battleshipSymbolLabel = (symbol) => ({
  '.': 'Undecided', W: 'Water', x: 'Ship; exact part undecided',
  S: 'Submarine', M: 'Middle of a ship',
  L: 'Left end; continues right', R: 'Right end; continues left',
  T: 'Top end; continues down', B: 'Bottom end; continues up',
}[symbol] || 'Undecided');

export function BattleshipSymbol({ symbol, hatchId }) {
  switch (symbol) {
    case 'W':
      return <path d="M12 23 q5 -5 10 0 t10 0" fill="none" stroke="#7195a4" strokeWidth="2.2" strokeLinecap="round" />;
    case 'x':
      return <rect x="9" y="9" width="26" height="26" rx="3" fill={`url(#${hatchId})`} stroke={INK} strokeWidth="1.4" />;
    case 'S':
      return <circle cx="22" cy="22" r="14" fill={INK} />;
    case 'M':
      return <rect x="8" y="8" width="28" height="28" fill={INK} />;
    case 'L':
      return <path d="M36 8 H22 A14 14 0 0 0 22 36 H36 Z" fill={INK} />;
    case 'R':
      return <path d="M8 8 H22 A14 14 0 0 1 22 36 H8 Z" fill={INK} />;
    case 'T':
      return <path d="M8 36 V22 A14 14 0 0 1 36 22 V36 Z" fill={INK} />;
    case 'B':
      return <path d="M8 8 V22 A14 14 0 0 0 36 22 V8 Z" fill={INK} />;
    default:
      return null;
  }
}

function FleetShip({ length }) {
  return (
    <svg aria-hidden="true" viewBox={`0 0 ${length * 12 + 4} 18`} width={length * 12 + 4} height="18">
      <rect x="2" y="3" width={length * 12} height="12" rx="6" fill={INK} />
      {Array.from({ length: length - 1 }, (_, index) => <line key={index} x1={14 + index * 12} y1="3" x2={14 + index * 12} y2="15" stroke="white" strokeWidth="1.5" />)}
    </svg>
  );
}

/** Display only: no propagation or candidate deductions happen in the browser. */
export default function BattleshipBoard({
  board, givens = [], rowTotals = [], columnTotals = [], fleet = [],
  changedCells = [], eliminatedCandidates = [], excludedCandidates = [],
  difficultyName = 'Unrated',
  ariaLabel = 'Battleship puzzle',
}) {
  const id = useId().replace(/:/g, '');
  if (!board || board.length !== 10 || board.some((row) => row.length !== 10)) {
    return <p className="py-16 text-center text-sm leading-6 text-slate-500">Load a puzzle to see its fleet and clues.</p>;
  }
  const changed = new Set(changedCells);
  const cells = board.flatMap((row, rowIndex) => [...row].map((symbol, column) => ({
    cell: rowIndex * 10 + column, row: rowIndex, column, symbol,
    given: Boolean(givens[rowIndex] && givens[rowIndex][column] !== '.'),
  })));
  const hatchId = `${id}-ship-hatch`;
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-center justify-between gap-3 text-xs leading-5 text-slate-600">
        <p>Numbers above and right count ship cells.</p>
        <p className="shrink-0 text-right font-semibold">Difficulty: {difficultyName}</p>
      </div>
      <div role="region" aria-label="Scrollable Battleship board" tabIndex="0" className="max-w-full overflow-x-auto rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan">
        <svg viewBox="0 0 504 505" role="img" aria-labelledby={`${id}-title ${id}-description`} className="h-auto w-full">
          <title id={`${id}-title`}>{ariaLabel}</title>
          <desc id={`${id}-description`}>Ten rows and columns. Blanks are undecided, waves are water, and striped squares are ship cells with their exact part undecided. Corner marks identify original clues. Blue double outlines show this step's changes. A dashed rust outline rejects one exact ship placement, without turning its cells into water. Row coordinates are on the left; column coordinates are below. A text version is in Board details.</desc>
          <defs>
            <pattern id={hatchId} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(35)">
              <rect width="6" height="6" fill="#edf2f5" />
              <line x1="0" y1="0" x2="0" y2="6" stroke={INK} strokeWidth="2" />
            </pattern>
          </defs>
          <rect width="504" height="505" fill="white" />
          {Array.from({ length: 10 }, (_, index) => (
            <g key={`totals-${index}`} textAnchor="middle" fontFamily="Arial, sans-serif">
              <text x={LEFT + index * SIZE + 22} y="25" fill={INK} fontSize="20" fontWeight="600" data-column-total={index}>{columnTotals[index]}</text>
              <text x="489" y={TOP + index * SIZE + 29} fill={INK} fontSize="20" fontWeight="600" data-row-total={index}>{rowTotals[index]}</text>
              <text x="13" y={TOP + index * SIZE + 27} fill="#64748b" fontSize="12">{index + 1}</text>
              <text x={LEFT + index * SIZE + 22} y="496" fill="#64748b" fontSize="12">{index + 1}</text>
            </g>
          ))}
          {Array.from({ length: 11 }, (_, index) => (
            <g key={`grid-${index}`} stroke="#b6c2ca" strokeWidth="1">
              <line x1={LEFT + index * SIZE} y1={TOP} x2={LEFT + index * SIZE} y2={TOP + 440} />
              <line x1={LEFT} y1={TOP + index * SIZE} x2={LEFT + 440} y2={TOP + index * SIZE} />
            </g>
          ))}
          <rect x={LEFT} y={TOP} width="440" height="440" fill="none" stroke={INK} strokeWidth="1.6" />
          {cells.map(({ cell, row, column, symbol, given }) => (
            <g key={cell} transform={`translate(${LEFT + column * SIZE} ${TOP + row * SIZE})`} data-cell-index={cell} data-symbol={symbol} data-original-given={given}>
              <title>{battleshipCellLabel(cell)}: {battleshipSymbolLabel(symbol)}{given ? '; original given' : ''}{changed.has(cell) ? '; changed this step' : ''}</title>
              <BattleshipSymbol symbol={symbol} hatchId={hatchId} />
              {given ? <path d="M3 3 H9 L3 9 Z" fill={INK} data-given-marker={cell} /> : null}
              {changed.has(cell) ? <g fill="none" stroke={BLUE} data-changed-cell={cell}><rect x="2" y="2" width="40" height="40" strokeWidth="1.7" /><rect x="4.5" y="4.5" width="35" height="35" strokeWidth="0.8" /></g> : null}
            </g>
          ))}
          {eliminatedCandidates.filter((candidate) => candidate.length).map((candidate) => {
            const rows = candidate.map((cell) => Math.floor(cell / 10));
            const columns = candidate.map((cell) => cell % 10);
            const x = LEFT + Math.min(...columns) * SIZE + 3;
            const y = TOP + Math.min(...rows) * SIZE + 3;
            const width = (Math.max(...columns) - Math.min(...columns) + 1) * SIZE - 6;
            const height = (Math.max(...rows) - Math.min(...rows) + 1) * SIZE - 6;
            return (
              <g key={candidate.join('-')} fill="none" stroke={RUST} strokeWidth="2.4" data-rejected-placement={candidate.join('-')}>
                <title>Rejected length-{candidate.length} ship: {candidate.map(battleshipCellLabel).join(', ')}. These cells may still belong to another ship.</title>
                <rect x={x} y={y} width={width} height={height} rx="3" strokeDasharray="7 4" />
                <path d={`M${x + width - 10} ${y + 12} l12 -12`} strokeLinecap="round" />
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-md bg-slate-50 px-2 py-2 text-sm text-slate-700" aria-label="Required fleet">
        {fleet.map(({ length, count }) => <span key={length} className="inline-flex items-center gap-1.5" aria-label={`${count} ${count === 1 ? 'ship' : 'ships'} of length ${length}`}><FleetShip length={length} /><span aria-hidden="true">× {count}</span></span>)}
      </div>
      <p className="text-xs leading-5 text-slate-600">Wave: water · Blank: undecided.{eliminatedCandidates.length ? ' Dashed outline: ruled-out ship position.' : ''}</p>
      <details className="border-t border-slate-200 pt-3 text-sm leading-6 text-slate-700">
        <summary className="cursor-pointer font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan">Board details and symbols</summary>
        <p className="mt-3 text-xs leading-5">Stripes mark a ship cell whose exact part is undecided. A small corner mark identifies a printed clue. Blue outlines show cells changed in the current step.</p>
        {eliminatedCandidates.length ? <p className="mt-3 text-xs leading-5">The dashed outline rules out this exact ship placement. Its cells may still be part of a different ship.</p> : null}
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
          {['S', 'L', 'R', 'T', 'B', 'M', 'x', 'W'].map((symbol) => <div className="flex items-center gap-2" key={symbol}><svg aria-hidden="true" viewBox="0 0 44 44" width="32" height="32" className="shrink-0"><BattleshipSymbol symbol={symbol} hatchId={hatchId} /></svg><span className="text-xs leading-5">{battleshipSymbolLabel(symbol)}</span></div>)}
        </div>
        <p className="mt-3 text-xs leading-5">R means row and C means column, counted from the top left. The fleet shows the required ships throughout the solve.</p>
        <p className="mt-2 text-xs leading-5">Row totals: {rowTotals.join(', ')}. Column totals: {columnTotals.join(', ')}.</p>
        {excludedCandidates.length ? <details className="mt-3"><summary className="cursor-pointer">Ruled-out placements so far ({excludedCandidates.length})</summary><ul className="mt-2 list-disc pl-5 text-xs">{excludedCandidates.map((candidate) => <li key={candidate.join('-')}>Length {candidate.length}: {candidate.map(battleshipCellLabel).join(', ')}</li>)}</ul></details> : null}
        <div className="mt-3 max-h-72 overflow-auto" tabIndex="0" role="region" aria-label="Current Battleship cells">
          <table className="w-full text-left text-xs leading-6">
            <caption className="sr-only">Exact current Battleship board</caption>
            <thead><tr><th scope="col">Cell</th><th scope="col">Current state</th></tr></thead>
            <tbody>{cells.map(({ cell, symbol, given }) => <tr key={cell} className="border-t border-slate-100"><th scope="row" className="font-medium">{battleshipCellLabel(cell)}</th><td>{battleshipSymbolLabel(symbol)}{given ? ' (original given)' : ''}{changed.has(cell) ? ' (changed this step)' : ''}</td></tr>)}</tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
